import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { command, filePath, read } from '@/services/vault/vault.http'
import {
  reviewPath,
  categoryLabels,
  serviceLabels,
  validAdoption,
  validCopyReceipt,
  type SourceAdoptionDescriptor,
  type SourceReviewInspection,
  type ReviewedCopyCommand,
  type ReviewedCopyReceipt,
} from '@/services/vault/sourceReviews'
import { CommandStatus, useCommand } from './VaultForms'

interface Props {
  client: string
  authority: string
  reviewKey: unknown[]
  view: SourceReviewInspection | undefined
  inspectionRevision: string | null
  permitted: boolean
  refresh: () => Promise<void>
}

/** Kept mounted across review refresh/selection so an unknown command is exact. */
export default function SourceAdoptionPanel({
  client,
  authority,
  reviewKey,
  view,
  inspectionRevision,
  permitted,
  refresh,
}: Props) {
  const actions = useCommand(),
    inFlight = useRef(false)
  const [ack, setAck] = useState<string | null>(null)
  const [original, setOriginal] = useState<ReviewedCopyCommand | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const preview = useQuery({
    queryKey: [...reviewKey, 'adoption', view?.review_id, view?.decision_id, inspectionRevision],
    queryFn: () =>
      read<SourceAdoptionDescriptor>(reviewPath(client, view!.review_id) + '/adoption'),
    enabled: !!view && view.state === 'approved' && permitted && !original,
    staleTime: 0,
  })
  const descriptor =
    permitted && !preview.isFetching && !preview.isError && validAdoption(preview.data, view)
      ? preview.data
      : undefined
  const binding = descriptor
    ? JSON.stringify([authority, client, descriptor, inspectionRevision, preview.dataUpdatedAt])
    : null
  useEffect(() => {
    setAck(null)
  }, [inspectionRevision, preview.isFetching, preview.isError, permitted])
  useEffect(() => {
    if (confirmed && original && view && view.review_id !== original.source_review_id) {
      setOriginal(null)
      setConfirmed(false)
    }
  }, [confirmed, original, view])

  const send = (payload: ReviewedCopyCommand) => {
    if (actions.busy || inFlight.current) return
    inFlight.current = true
    setAck(null)
    setOriginal(payload)
    setConfirmed(false)
    void actions.run(async () => {
      try {
        const receipt = await command<ReviewedCopyReceipt>(
          filePath(client) + '/legacy-publications',
          payload
        )
        if (!validCopyReceipt(receipt, client))
          throw new Error(
            'The copy receipt could not be verified. Retry the original copy request.'
          )
        setConfirmed(true)
        // Exact historical receipts say nothing about current approval/access.
        await refresh()
      } finally {
        inFlight.current = false
      }
    })
  }

  if (!original && (!view || view.state !== 'approved' || !permitted)) return null
  return (
    <section className="card card-elevated p-5 space-y-4" aria-label="Copy approved document">
      <h2 className="font-semibold">Copy approved document to client vault</h2>
      <p className="text-sm text-text-secondary">
        The original firm document is preserved. The client copy uses the approved interpretation
        and must pass scanning before it becomes available.
      </p>
      {!original && (
        <>
          {preview.isFetching && (
            <p role="status">Checking the approved source and current ownership…</p>
          )}
          {preview.isError && <p role="alert">{preview.error.message}</p>}
          {!preview.isFetching && !preview.isError && preview.data && !descriptor && (
            <p role="alert">
              The copy preview does not match this exact approval. Refresh reviews.
            </p>
          )}
          {descriptor && (
            <>
              <p className="text-sm text-text-secondary">
                The ownership, service, category and financial year are fixed to the interpretation
                shown above. Current copying authorization will be checked when you submit.
              </p>
              <label className="flex items-start gap-2 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={ack === binding}
                  disabled={actions.busy}
                  onChange={event => setAck(event.target.checked ? binding : null)}
                />
                I approve copying this exact reviewed document into the client vault.
              </label>
              <button
                className="btn btn-primary"
                disabled={actions.busy || ack !== binding}
                onClick={() => {
                  if (!descriptor || !binding || ack !== binding) return
                  const payload = {
                    source_document_id: descriptor.source_document_id,
                    source_review_id: descriptor.review_id,
                    source_decision_id: descriptor.decision_id,
                    approve_portability: true as const,
                    metadata: descriptor.metadata,
                  }
                  send({
                    ...payload,
                    metadata: {
                      ...payload.metadata,
                      idempotency_key: actions.idempotencyKey({ client, authority, ...payload }),
                    },
                  })
                }}
              >
                Copy approved document
              </button>
            </>
          )}
        </>
      )}
      {original && !confirmed && (
        <>
          <p className="text-sm text-text-secondary">
            Original request: {categoryLabels[original.metadata.category]} ·{' '}
            {serviceLabels[original.metadata.service]} ·{' '}
            {original.metadata.financial_year ?? 'Undated'}.
          </p>
          <p className="text-sm text-text-secondary">
            The original request is awaiting confirmation. Retry that exact request to check its
            outcome; no new request will be created automatically.
          </p>
          <button
            className="btn btn-secondary"
            disabled={actions.busy}
            onClick={() => send(original)}
          >
            Retry original copy request
          </button>
        </>
      )}
      {confirmed && (
        <p role="status" className="text-sm text-text-secondary">
          Copy request confirmed and submitted for scanning. Check Files for its current scan and
          availability status.
        </p>
      )}
      <CommandStatus {...actions} />
    </section>
  )
}
